import React from 'react';
import { motion } from 'framer-motion';
import type { Icon } from '@phosphor-icons/react';
import { MOTION } from '../../../lib/immobilien/motion';
import { useRasterMass } from './WidgetGrid';

/**
 * Widget im Raster (README §4.5/4.6): Radius 26, Padding 16, Glas oder Akzent.
 * Größe: normal 1×1, breit 2×1, hoch 1×2, gross 2×2.
 * Mit layoutId morpht das Widget in das MorphPanel mit derselben layoutId.
 */
export type WidgetGroesse = 'normal' | 'breit' | 'hoch' | 'gross';

const SPANN: Record<WidgetGroesse, React.CSSProperties> = {
  normal: {},
  breit: { gridColumn: 'span 2' },
  hoch: { gridRow: 'span 2' },
  gross: { gridColumn: 'span 2', gridRow: 'span 2' },
};

export interface WidgetProps {
  titel: string;
  icon: Icon;
  /** Rechts im Kopf, z. B. „Okt 2026“ oder ein Chip. */
  rechts?: React.ReactNode;
  groesse?: WidgetGroesse;
  /** Akzent-Fläche (Gesamtmiete, Kaltmiete). */
  akzent?: boolean;
  /** Klick öffnet Panel/Bereich. Ohne Handler ist das Widget nicht klickbar. */
  onOeffnen?: () => void;
  layoutId?: string;
  /** Kopf ausblenden (z. B. Foto-Widget). */
  ohneKopf?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function Widget({
  titel, icon: IconKomp, rechts, groesse = 'normal', akzent = false, onOeffnen, layoutId, ohneKopf = false, className = '', style, children,
}: WidgetProps) {
  const klickbar = onOeffnen !== undefined;
  // Im gepackten Raster bestimmt die Zelle die Größe; sonst die eigene Spannweite
  const imRaster = useRasterMass() !== null;
  const tastatur = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onOeffnen || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(); }
  };
  return (
    <motion.div
      layoutId={layoutId}
      transition={{ layout: MOTION.morph }}
      role={klickbar ? 'button' : undefined}
      tabIndex={klickbar ? 0 : undefined}
      aria-label={klickbar ? `${titel} öffnen` : undefined}
      onClick={onOeffnen}
      onKeyDown={tastatur}
      className={`${akzent ? 'immo-akzent' : 'immo-glas'} ${klickbar ? 'immo-hover-widget cursor-pointer' : ''} relative flex flex-col overflow-hidden min-w-0 ${className}`}
      style={{ borderRadius: 26, padding: 16, ...(imRaster ? {} : SPANN[groesse]), ...style }}
    >
      {!ohneKopf && (
        <div className="flex items-center justify-between min-w-0" style={{ gap: 8 }}>
          <span className="flex items-center whitespace-nowrap" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: akzent ? 'var(--acc-ink)' : 'var(--acc)' }}>
            <IconKomp weight="fill" size={15} />
            {titel}
          </span>
          {rechts !== undefined && (
            <span className="min-w-0 truncate" style={{ fontSize: 12, color: akzent ? 'var(--acc-ink2)' : 'var(--ink3)' }}>{rechts}</span>
          )}
        </div>
      )}
      {children}
    </motion.div>
  );
}

/** Großer Wert unten im Widget (README §4.4: 24–28 px). */
export function WidgetWert({ wert, sub, farbe, akzent = false }: { wert: React.ReactNode; sub?: React.ReactNode; farbe?: string; akzent?: boolean }) {
  return (
    <div className="flex flex-col" style={{ marginTop: 'auto', gap: 5 }}>
      <span className="immo-kpi" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.03em', color: farbe ?? (akzent ? 'var(--acc-ink)' : 'var(--ink)') }}>{wert}</span>
      {sub !== undefined && (
        <span className="truncate" style={{ fontSize: 12, fontWeight: 500, color: akzent ? 'var(--acc-ink2)' : 'var(--ink3)' }}>{sub}</span>
      )}
    </div>
  );
}
